import React, { useState, useRef } from 'react';
import { Button, Form, Table, Alert, ProgressBar, Card, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faFileAlt, faExclamationTriangle, faDownload, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { withAuth } from '../../api/withAuth';
import { Events } from '../../firebase/telemetry';
import { logError } from '../../firebase/errorReporting';

const GoldFinderApi = withAuth(axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_API_URL
}));

const SoilCorrectionImport = ({ onImportComplete }) => {
    const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Results
    const [file, setFile] = useState(null);
    const [validationResult, setValidationResult] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [applyResult, setApplyResult] = useState(null);
    const fileInputRef = useRef(null);

    const resetToStep1 = () => {
        setStep(1);
        setFile(null);
        setValidationResult(null);
        setPreviewData(null);
        setSelectedRows(new Set());
        setApplyResult(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile && selectedFile.type === 'text/csv') {
            setFile(selectedFile);
        } else {
            alert('Please select a valid CSV file');
            event.target.value = '';
        }
    };

    const validateCSV = async () => {
        if (!file) return;
        
        setLoading(true);
        const formData = new FormData();
        formData.append('csvFile', file);

        try {
            const response = await GoldFinderApi.post('/admin/soil-corrections/validate', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setValidationResult(response.data);
            
            if (response.data.success) {
                // Auto-select all valid rows
                const allRowIds = new Set(response.data.validData.map((_, index) => index));
                setSelectedRows(allRowIds);
                setStep(2);
                await generatePreview(response.data.validData);
            }
        } catch (error) {
            console.error('Error validating CSV:', error);
            setValidationResult({
                success: false,
                errors: [{ message: 'Failed to validate CSV file' }]
            });
        } finally {
            setLoading(false);
        }
    };

    const generatePreview = async (validData) => {
        try {
            const response = await GoldFinderApi.post('/admin/soil-corrections/preview', {
                corrections: validData
            });

            setPreviewData(response.data);
        } catch (error) {
            console.error('Error generating preview:', error);
        }
    };

    const toggleRowSelection = (index) => {
        const newSelected = new Set(selectedRows);
        if (newSelected.has(index)) {
            newSelected.delete(index);
        } else {
            newSelected.add(index);
        }
        setSelectedRows(newSelected);
    };

    const toggleSelectAll = () => {
        if (selectedRows.size === validationResult.validData.length) {
            setSelectedRows(new Set());
        } else {
            const allRowIds = new Set(validationResult.validData.map((_, index) => index));
            setSelectedRows(allRowIds);
        }
    };

    const applyCorrections = async () => {
        setLoading(true);
        
        try {
            const selectedCorrections = validationResult.validData.filter((_, index) => 
                selectedRows.has(index)
            );

            const response = await GoldFinderApi.post('/admin/soil-corrections/apply', {
                corrections: selectedCorrections
            });

            Events.adminCorrectionApplied('soil', selectedCorrections.length);
            setApplyResult(response.data);
            setStep(3);
            
            // Notify parent component
            if (onImportComplete) {
                onImportComplete();
            }
        } catch (error) {
            console.error('Error applying corrections:', error);
            logError(error, { component: 'SoilCorrectionImport', action: 'apply' });
            setApplyResult({
                success: false,
                error: 'Failed to apply corrections'
            });
        } finally {
            setLoading(false);
        }
    };

    const downloadTemplate = () => {
        const template = 'ANumber,Company Sample ID,au ppb\n12345,ABC-001,150.5\n12346,ABC-002,75.2\n';
        const blob = new Blob([template], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'soil_corrections_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    if (step === 1) {
        return (
            <Card>
                <Card.Header>
                    <h5><FontAwesomeIcon icon={faUpload} className="me-2" />Step 1: Upload CSV File</h5>
                </Card.Header>
                <Card.Body>
                    <div className="mb-3">
                        <Alert variant="info">
                            <strong>Required CSV Format:</strong>
                            <ul className="mb-0 mt-2">
                                <li>Headers: <code>ANumber</code>, <code>Company Sample ID</code>, <code>au ppb</code></li>
                                <li>ANumber: Must be non-empty</li>
                                <li>Company Sample ID: Must be non-empty</li>
                                <li>au ppb: Must be a valid positive number</li>
                            </ul>
                        </Alert>
                        
                        <Button variant="outline-secondary" size="sm" onClick={downloadTemplate} className="mb-3">
                            <FontAwesomeIcon icon={faDownload} className="me-1" />
                            Download Template
                        </Button>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>Select CSV File</Form.Label>
                        <Form.Control
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                        />
                    </Form.Group>

                    {file && (
                        <div className="mb-3">
                            <p><strong>Selected file:</strong> {file.name} ({(file.size / 1024).toFixed(1)} KB)</p>
                        </div>
                    )}

                    {validationResult && !validationResult.success && (
                        <Alert variant="danger">
                            <strong>Validation Errors:</strong>
                            <ul className="mb-0 mt-2">
                                {validationResult.errors.map((error, index) => (
                                    <li key={index}>
                                        {error.row ? `Row ${error.row}: ` : ''}
                                        {error.message}
                                    </li>
                                ))}
                                {validationResult.duplicates && validationResult.duplicates.map((dup, index) => (
                                    <li key={`dup-${index}`}>
                                        Row {dup.row}: Duplicate ANumber {dup.anumber} with Company Sample ID {dup.company_sample_id}
                                    </li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    <div className="d-flex gap-2">
                        <Button
                            variant="primary"
                            onClick={validateCSV}
                            disabled={!file || loading}
                        >
                            {loading ? (
                                <>
                                    <ProgressBar animated now={100} style={{ height: '20px', width: '100px' }} />
                                    Validating...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                                    Validate & Continue
                                </>
                            )}
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    if (step === 2) {
        return (
            <Card>
                <Card.Header>
                    <h5><FontAwesomeIcon icon={faCheck} className="me-2" />Step 2: Preview Changes</h5>
                </Card.Header>
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Alert variant="success">
                                <strong>Validation Successful!</strong><br />
                                {validationResult.totalRows} total rows, {validationResult.validRows} valid rows
                            </Alert>
                        </Col>
                        <Col md={6}>
                            {previewData && (
                                <Card>
                                    <Card.Body>
                                        <strong>Preview Summary:</strong>
                                        <ul className="mb-0 mt-1">
                                            <li>Updates: {previewData.summary.updates}</li>
                                            <li>New records: {previewData.summary.new_records}</li>
                                            <li>Not found in dataset: {previewData.summary.not_found}</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            )}
                        </Col>
                    </Row>

                    <div className="mb-3">
                        <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={toggleSelectAll}
                            className="me-2"
                        >
                            {selectedRows.size === validationResult.validData.length ? 'Deselect All' : 'Select All'}
                        </Button>
                        <Badge bg="secondary">
                            {selectedRows.size} of {validationResult.validData.length} rows selected
                        </Badge>
                    </div>

                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <Table striped bordered hover size="sm">
                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                                <tr>
                                    <th width="5%">
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedRows.size === validationResult.validData.length}
                                            onChange={toggleSelectAll}
                                        />
                                    </th>
                                    <th>ANumber</th>
                                    <th>Company Sample ID</th>
                                    <th>Current au ppb</th>
                                    <th>New au ppb</th>
                                    <th>Action</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {validationResult.validData.map((row, index) => {
                                    const preview = previewData?.preview.find(p => 
                                        p.anumber === row.anumber && p.company_sample_id === row.company_sample_id
                                    );
                                    
                                    return (
                                        <tr key={index} className={selectedRows.has(index) ? 'table-primary' : ''}>
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedRows.has(index)}
                                                    onChange={() => toggleRowSelection(index)}
                                                />
                                            </td>
                                            <td>{row.anumber}</td>
                                            <td>{row.company_sample_id}</td>
                                            <td>{preview?.current_au_ppb || 'N/A'}</td>
                                            <td>{row.au_ppb}</td>
                                            <td>
                                                <Badge bg={preview?.action === 'update' ? 'warning' : 'success'}>
                                                    {preview?.action === 'update' ? 'Update' : 'New'}
                                                </Badge>
                                            </td>
                                            <td>
                                                {preview?.found_in_dataset ? (
                                                    <Badge bg="success">Found</Badge>
                                                ) : (
                                                    <Badge bg="warning">Not Found</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex gap-2 mt-3">
                        <Button variant="secondary" onClick={resetToStep1}>
                            <FontAwesomeIcon icon={faTimes} className="me-1" />
                            Cancel
                        </Button>
                        <Button
                            variant="success"
                            onClick={applyCorrections}
                            disabled={selectedRows.size === 0 || loading}
                        >
                            {loading ? (
                                <>
                                    <ProgressBar animated now={100} style={{ height: '20px', width: '100px' }} />
                                    Applying...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                    Apply {selectedRows.size} Corrections
                                </>
                            )}
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    if (step === 3) {
        return (
            <Card>
                <Card.Header>
                    <h5>
                        <FontAwesomeIcon 
                            icon={applyResult?.success ? faCheck : faExclamationTriangle} 
                            className="me-2" 
                        />
                        Step 3: Results
                    </h5>
                </Card.Header>
                <Card.Body>
                    {applyResult?.success ? (
                        <Alert variant="success">
                            <strong>Import Successful!</strong><br />
                            {applyResult.message}
                        </Alert>
                    ) : (
                        <Alert variant="danger">
                            <strong>Import Failed!</strong><br />
                            {applyResult?.error || 'Unknown error occurred'}
                        </Alert>
                    )}

                    <div className="d-flex gap-2">
                        <Button variant="primary" onClick={resetToStep1}>
                            Import Another File
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }
};

export default SoilCorrectionImport;